import { useCallback, useEffect, useState } from 'react'

import queryString, { ParsedQuery } from 'query-string'
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom'

export const OMITTED_PAGINATION_PARAMS = ['order', 'start_index', 'max_count']

type UpdateQueryParams = {
  addParam: (key: string, value: string) => void
  deleteParams: (...paramsToDelete: { key: string; value: string | null }[]) => void
  params: ParsedQuery
}

export type BeforeNavigate =
  | ((urlParams: ParsedQuery, navigate: NavigateFunction) => boolean)
  | null

export type Options = {
  pathname?: string
  beforeNavigate?: BeforeNavigate
}

const useUpdateQueryParams = ({
  pathname = location.pathname,
  beforeNavigate,
}: Options = {}): UpdateQueryParams => {
  const location = useLocation()
  const navigate = useNavigate()
  const [params, setParams] = useState<ParsedQuery>(parseURL())

  const updateSearchQuery = (urlParams: ParsedQuery): void => {
    let shouldNavigate = true
    if (typeof beforeNavigate === 'function') shouldNavigate = beforeNavigate(urlParams, navigate)
    if (shouldNavigate) {
      navigate({
        pathname,
        search: queryString.stringify(urlParams, { arrayFormat: 'comma' }),
      })
    }
  }

  const addParam = useCallback(
    (key: string, value: string) => {
      const urlParams = queryString.parse(location.search, {
        arrayFormat: 'comma',
      })
      const filtersForCategory = urlParams[key]

      if (!filtersForCategory) urlParams[key] = [value]
      else if (Array.isArray(filtersForCategory)) {
        if (!filtersForCategory.includes(value)) {
          filtersForCategory.push(value)
        }
      } else urlParams[key] = [filtersForCategory, value]

      delete urlParams.page
      updateSearchQuery(urlParams)
    },
    [location, params]
  )

  const deleteParams = useCallback(
    (...paramsToDelete: { key: string; value: string | null }[]) => {
      const urlParams = queryString.parse(location.search, {
        arrayFormat: 'comma',
      })

      for (const { key, value } of paramsToDelete) {
        if (!Array.isArray(urlParams[key])) {
          urlParams[key] = [urlParams[key] as string]
        }
        const filtersForCategory = urlParams[key]

        if (Array.isArray(filtersForCategory)) {
          urlParams[key] = value === null ? [] : filtersForCategory?.filter((v) => v !== value)
          if (!urlParams[key]?.length) {
            delete urlParams[key]
          }
        }
      }

      delete urlParams.page
      updateSearchQuery(urlParams)
    },
    [location, params]
  )

  useEffect(() => {
    setParams(parseURL())
  }, [location])

  return { addParam, deleteParams, params }
}

export const parseURL = (search = location.search): ParsedQuery => {
  const queryParams = queryString.parse(search, {
    arrayFormat: 'comma',
  })

  for (const key in queryParams) {
    if (OMITTED_PAGINATION_PARAMS.includes(key)) {
      delete queryParams[key]
    } else if (!Array.isArray(queryParams[key])) {
      queryParams[key] = [queryParams[key] as string]
    }
  }
  return queryParams ? queryParams : {}
}

export default useUpdateQueryParams
